import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
});

const STATUS_STYLE = {
  ready: 'bg-green-100 text-green-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  timed_out: 'bg-amber-100 text-amber-800',
  skipped: 'bg-amber-100 text-amber-800',
};

const MetricCard = ({ label, value, hint, testId }) => (
  <div className="border border-slate-200 rounded-sm p-3" data-testid={testId}>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-lg font-[700] text-[#003366]">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
  </div>
);

/** Stage 0 evidence: runs, frozen snapshot, advisory metrics and the audit trail. */
export const EvidenceTab = ({ assessmentId }) => {
  const [audit, setAudit] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/admin/evidence/${assessmentId}/audit`, authHeaders()),
        axios.get(`${API}/admin/evidence/${assessmentId}/summary`, authHeaders()),
      ]);
      setAudit(a.data);
      setSummary(s.data);
    } catch (e) {
      toast.error('Could not load the Stage 0 evidence trail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assessmentId]);

  if (!assessmentId) {
    return <p className="p-4 text-sm text-slate-500" data-testid="evidence-tab-empty">
      Select an assessment to see its Stage 0 evidence trail.
    </p>;
  }

  const consent = audit?.consent_summary;
  const metrics = summary?.metrics || {};
  const snap = summary?.snapshot;
  const pillars = metrics.pillars || {};

  return (
    <div className="p-4 space-y-6" data-testid="evidence-audit-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[700] text-[#003366]">Stage 0 Evidence</h3>
          <p className="text-sm text-slate-500">
            {summary?.discovery_status || '—'} · outcome {summary?.discovery_outcome || '—'} ·{' '}
            {audit?.events?.length || 0} audit event(s) · tokens deleted {consent?.tokens_deleted ?? 0}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}
          className="rounded-sm" data-testid="evidence-audit-refresh-btn">
          Refresh
        </Button>
      </div>

      {/* Advisory metrics */}
      <div data-testid="evidence-metrics-block">
        <h4 className="text-sm font-[700] text-slate-700 mb-2">
          Evidence metrics <span className="font-[400] text-slate-500">— advisory only, separate from the DPDP score</span>
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="Evidence confidence"
            value={metrics.evidence_confidence_pct === null || metrics.evidence_confidence_pct === undefined
              ? '—' : `${metrics.evidence_confidence_pct}%`}
            hint={metrics.evidence_confidence_label} testId="evidence-metric-confidence" />
          <MetricCard label="Evidence coverage"
            value={metrics.evidence_coverage_pct === null || metrics.evidence_coverage_pct === undefined
              ? '—' : `${metrics.evidence_coverage_pct}%`}
            hint="Checkable answers a scan could speak to" testId="evidence-metric-coverage" />
          <MetricCard label="Checkable scope"
            value={metrics.checkable_scope
              ? `${metrics.checkable_scope.checkable_answered}/${metrics.checkable_scope.total_answered}`
              : '—'}
            hint="Answered questions checkable by Stage 0" testId="evidence-metric-scope" />
          <MetricCard label="Mismatches"
            value={(metrics.mismatches || []).length}
            hint="Hints overridden with a reason" testId="evidence-metric-mismatches" />
        </div>
      </div>

      {/* Runs */}
      <div data-testid="evidence-runs-block">
        <h4 className="text-sm font-[700] text-slate-700 mb-2">Scan runs</h4>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Type</TableHead>
              <TableHead>Target / provider</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Coverage</TableHead>
              <TableHead>Flags</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Completed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(summary?.runs || []).map((r) => (
              <TableRow key={r.run_id} data-testid={`evidence-run-row-${r.run_id}`}>
                <TableCell className="capitalize">{r.type}</TableCell>
                <TableCell className="text-xs">{r.target || r.provider || '—'}</TableCell>
                <TableCell>
                  <Badge className={STATUS_STYLE[r.status] || 'bg-slate-100 text-slate-700'}>{r.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  {r.coverage?.files_opened != null
                    ? `${r.coverage.files_opened}/${r.coverage.files_enumerated} files`
                    : `${r.progress?.pages_scanned ?? 0} pages · ${r.findings_count} checks`}
                </TableCell>
                <TableCell className="text-xs">
                  {r.is_stub && <Badge className="bg-blue-100 text-blue-700 mr-1">stub</Badge>}
                  {r.is_late && <Badge className="bg-amber-100 text-amber-800 mr-1">late</Badge>}
                  {r.partial && <Badge className="bg-amber-100 text-amber-800">partial</Badge>}
                </TableCell>
                <TableCell className="font-mono text-xs">{r.started_at}</TableCell>
                <TableCell className="font-mono text-xs">{r.completed_at || '—'}</TableCell>
              </TableRow>
            ))}
            {(!summary?.runs || summary.runs.length === 0) && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">
                No Stage 0 scan runs for this assessment
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Snapshot */}
      <div data-testid="evidence-snapshot-block">
        <h4 className="text-sm font-[700] text-slate-700 mb-2">Frozen snapshot</h4>
        {snap ? (
          <div className="border border-slate-200 rounded-sm p-3 text-sm text-slate-700 space-y-1">
            <div className="font-mono text-xs text-slate-500">{snap.snapshot_id}</div>
            <div>
              outcome <strong>{snap.outcome}</strong> · frozen {snap.frozen_at} ·{' '}
              {snap.findings_count} finding(s) · {snap.hint_count} hint chip(s) ·{' '}
              {snap.vendor_count} vendor(s) · {snap.flow_count} data flow(s)
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {Object.entries(snap.steps || {}).map(([step, status]) => (
                <Badge key={step} className={STATUS_STYLE[status] || 'bg-slate-100 text-slate-700'}>
                  {step}: {status}
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Evidence has not been frozen for this assessment.</p>
        )}
      </div>

      {/* Per-pillar provenance */}
      {Object.keys(pillars).length > 0 && (
        <div data-testid="evidence-provenance-block">
          <h4 className="text-sm font-[700] text-slate-700 mb-2">Answer provenance by pillar</h4>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Pillar</TableHead>
                <TableHead>Answered</TableHead>
                <TableHead>Checkable</TableHead>
                <TableHead>Hint accepted</TableHead>
                <TableHead>Hint overridden</TableHead>
                <TableHead>No evidence</TableHead>
                <TableHead>Self declared</TableHead>
                <TableHead>Confidence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(pillars).map(([code, p]) => (
                <TableRow key={code} data-testid={`evidence-provenance-row-${code}`}>
                  <TableCell className="font-[600]">{code}</TableCell>
                  <TableCell>{p.answered}</TableCell>
                  <TableCell>{p.eligible}</TableCell>
                  <TableCell>{p.accepted}</TableCell>
                  <TableCell>{p.overridden}</TableCell>
                  <TableCell>{p.no_evidence}</TableCell>
                  <TableCell>{p.self_declared}</TableCell>
                  <TableCell>{p.pillar_confidence_pct === null ? '—' : `${p.pillar_confidence_pct}%`}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Limitations */}
      {(metrics.scan_coverage?.limitations || []).length > 0 && (
        <div data-testid="evidence-limitations-block">
          <h4 className="text-sm font-[700] text-slate-700 mb-2">Scan coverage &amp; limitations</h4>
          <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
            {metrics.scan_coverage.limitations.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </div>
      )}

      {/* Audit trail */}
      <div data-testid="evidence-audit-block">
        <h4 className="text-sm font-[700] text-slate-700 mb-2">
          Connector audit trail · providers consented: {consent?.providers_consented?.join(', ') || 'none'}
        </h4>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>At</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(audit?.events || []).map((e) => (
              <TableRow key={e.event_id}>
                <TableCell className="font-mono text-xs">{e.at}</TableCell>
                <TableCell className="capitalize">{e.provider}</TableCell>
                <TableCell><Badge className="bg-slate-100 text-slate-700">{e.event}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{(e.scopes || []).length}</TableCell>
                <TableCell className="text-xs text-slate-500">{e.ip || '-'}</TableCell>
              </TableRow>
            ))}
            {(!audit?.events || audit.events.length === 0) && (
              <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">
                No Stage 0 evidence events for this assessment
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default EvidenceTab;
